/**
 * Telegram Mini App SDK — docs/TELEGRAM.md.
 *
 * SDK'ni Telegram o'zi beradi (`telegram-web-app.js`), npm paketi emas.
 * Bu fayl uning ustiga tipli va tor qatlam qo'yadi — shunda `any` tarqalmaydi
 * va qaysi API ishlatilayotgani bir joydan ko'rinadi.
 */

import { THEME_BG } from '@voice/ui-tokens';

export interface TelegramUserUnsafe {
  id: number;
  first_name?: string;
  username?: string;
  language_code?: string;
}

interface BackButton {
  show(): void;
  hide(): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
}

interface HapticFeedback {
  impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
  notificationOccurred(type: 'error' | 'success' | 'warning'): void;
  selectionChanged(): void;
}

/** Qurilma yoki Telegram egallagan chekka (Bot API 8.0). */
interface SafeAreaInset {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

interface CloudStorage {
  setItem(key: string, value: string, cb?: (err: string | null, ok?: boolean) => void): void;
  getItem(key: string, cb: (err: string | null, value?: string) => void): void;
  removeItem(key: string, cb?: (err: string | null, ok?: boolean) => void): void;
}

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: { user?: TelegramUserUnsafe };
  version: string;
  platform: string;
  viewportStableHeight: number;
  viewportHeight: number;
  isExpanded: boolean;
  BackButton: BackButton;
  HapticFeedback: HapticFeedback;
  CloudStorage: CloudStorage;
  /* ── Fullscreen (Bot API 8.0) ──
   * Hammasi ixtiyoriy: eski klientda ular UMUMAN yo'q va `?.` bilan
   * chaqiriladi. `isVersionAtLeast('8.0')` — birinchi qo'riqchi. */
  isFullscreen?: boolean;
  safeAreaInset?: SafeAreaInset;
  contentSafeAreaInset?: SafeAreaInset;
  requestFullscreen?(): void;
  exitFullscreen?(): void;
  ready(): void;
  expand(): void;
  close(): void;
  isVersionAtLeast(v: string): boolean;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
  enableClosingConfirmation(): void;
  disableClosingConfirmation(): void;
  openInvoice(url: string, cb?: (status: string) => void): void;
  openLink(url: string, options?: { try_instant_view?: boolean }): void;
  onEvent(event: string, cb: () => void): void;
  offEvent(event: string, cb: () => void): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export const tg: TelegramWebApp | undefined = window.Telegram?.WebApp;

/**
 * Telegram ichidamizmi.
 *
 * Bu tekshiruv UX uchun, XAVFSIZLIK uchun emas (docs/SECURITY.md §5) —
 * uni chetlab o'tish oson. Yagona haqiqiy chegara — serverdagi HMAC.
 */
export const insideTelegram = Boolean(tg?.initData);

/** Serverga yuboriladigan xom `initData`. `initDataUnsafe` ga ishonilmaydi. */
export function getInitData(): string {
  return tg?.initData ?? '';
}

/**
 * Balandlikni CSS o'zgaruvchilariga yozadi — **ikkitasi**.
 *
 * `100vh` va `100dvh` Telegram ichida ishonchsiz (docs/TELEGRAM.md).
 * Klaviatura ochilganda `viewportChanged` keladi va qiymatlar
 * yangilanadi.
 *
 *   `--tg-viewport-stable-height`  klaviaturasiz balandlik. Oddiy
 *                                  ekranlar shuni oladi — u klaviatura
 *                                  ochilganda SAKRAMAYDI.
 *   `--tg-viewport-height`         AYNI PAYTDAGI ko'rinadigan balandlik.
 *                                  Klaviatura ochilganda kichrayadi.
 *
 * Ikkinchisi yozishma ekrani uchun: u pastiga yopishgan kiritish
 * paneliga ega va barqaror balandlikka tayansa panel klaviatura
 * ORTIDA qolib ketardi — ya'ni yozayotgan odam nima yozayotganini
 * ko'rmasdi. Qolgan ekranlarda esa aksincha: "sakramaslik" muhimroq.
 */
function syncViewport(): void {
  const stable = tg?.viewportStableHeight ?? window.innerHeight;

  /* Klaviatura balandlikni IKKI XIL yo'l bilan bildiradi va platformalar
   * bo'yicha faqat bittasi ishlaydi: Android'da webview'ning o'zi
   * kichrayadi (`viewportHeight`), iOS'da esa webview joyida qoladi va
   * faqat `visualViewport` kichrayadi. Kichigi olinadi — panel
   * ikkalasida ham klaviatura ustida qoladi. */
  const fromTelegram = tg?.viewportHeight ?? window.innerHeight;
  const fromBrowser = window.visualViewport?.height ?? Number.POSITIVE_INFINITY;
  const current = Math.min(fromTelegram, fromBrowser);

  document.documentElement.style.setProperty('--tg-viewport-stable-height', `${stable}px`);
  document.documentElement.style.setProperty('--tg-viewport-height', `${current}px`);
}

/**
 * Ekranning tepasida qancha joy band — `--tg-safe-top`.
 *
 * **Ikkita inset qo'shiladi va bittasi yetmaydi:**
 *   `safeAreaInset`         qurilmaniki — notch, status bar
 *   `contentSafeAreaInset`  Telegram'niki — fullscreen'da tepada suzib
 *                           turadigan yopish va menyu tugmalari
 *
 * Fullscreen bo'lmasa ikkalasi ham 0: oddiy rejimda Telegram sarlavhasi
 * webview'dan TASHQARIDA va webview'ning tepasi allaqachon kontent
 * tepasi.
 */
function syncSafeArea(): void {
  const device = tg?.safeAreaInset?.top ?? 0;
  const content = tg?.contentSafeAreaInset?.top ?? 0;
  document.documentElement.style.setProperty('--tg-safe-top', `${device + content}px`);
}

/**
 * Haqiqiy fullscreen — Bot API **8.0** dan.
 *
 * `expand()` bilan adashtirmaslik kerak: u faqat webview'ni to'liq
 * balandlikka cho'zadi, Telegram sarlavhasi esa tepada joyida qoladi.
 * `requestFullscreen()` sarlavhani ham olib tashlaydi va ilova butun
 * ekranni egallaydi — o'shanda tepani `safeAreaInset` bilan o'zimiz
 * hisoblashimiz shart.
 */
export function supportsFullscreen(): boolean {
  return Boolean(tg?.isVersionAtLeast('8.0'));
}

let viewportBound = false;

export function initTelegram(): void {
  // `visualViewport` — klaviatura ochilganini bildiruvchi yagona manba
  // iOS'da. Telegram ichida ham, brauzerda ham bir xil kerak.
  window.visualViewport?.addEventListener('resize', syncViewport);

  if (!tg) {
    // Brauzerda ochilgan — dev rejimi. Balandlikni oynadan olamiz.
    syncViewport();
    window.addEventListener('resize', syncViewport);
    return;
  }

  tg.ready();
  tg.expand();
  // Fullscreen `expand()` DAN KEYIN: eski klientda birinchisi baribir
  // ishlaydi va ilova to'liq balandlikda ochiladi.
  if (supportsFullscreen()) tg.requestFullscreen?.();
  tg.setHeaderColor(THEME_BG);
  tg.setBackgroundColor(THEME_BG);

  syncViewport();
  syncSafeArea();
  if (!viewportBound) {
    tg.onEvent('viewportChanged', syncViewport);
    // Fullscreen'ga kirish/chiqish balandlikni HAM, insetlarni HAM
    // o'zgartiradi — ikkalasi ham qayta hisoblanadi.
    tg.onEvent('fullscreenChanged', syncBoth);
    tg.onEvent('safeAreaChanged', syncSafeArea);
    tg.onEvent('contentSafeAreaChanged', syncSafeArea);
    viewportBound = true;
  }
}

function syncBoth(): void {
  syncViewport();
  syncSafeArea();
}

/**
 * Yangi API'lar 6.1 dan boshlab bor.
 *
 * Tekshiruvsiz chaqirilsa SDK har bosishda konsolga ogohlantirish yozadi
 * ("... is not supported in version 6.0") va bu haqiqiy xatolarni ko'mib
 * yuboradi. docs/TELEGRAM.md: yangi API ishlatishdan oldin versiyani
 * tekshiring.
 */
export function supportsModernApi(): boolean {
  return Boolean(tg?.isVersionAtLeast('6.1'));
}

/**
 * `CloudStorage` — Bot API **6.9** dan.
 *
 * ⚠️ Bu tekshiruv `haptic` dagidek "konsol toza bo'lsin" uchun emas.
 * Eski klientda `tg.CloudStorage` OBYEKT sifatida mavjud bo'ladi (SDK uni
 * har doim e'lon qiladi), lekin `getItem` chaqirilganda klient hech narsa
 * qaytarmaydi — **callback hech qachon chaqirilmaydi**. Callbackni
 * `Promise` ga o'ragan joy esa abadiy kutib qoladi.
 *
 * Aynan shu ilovaning kirish ekranini "Yuklanmoqda…" da qotirib qo'ygan
 * edi: xato ham chiqmasdi, chunki hech narsa yiqilmagan — shunchaki
 * hech qachon tugamagan.
 */
export function supportsCloudStorage(): boolean {
  return Boolean(tg?.isVersionAtLeast('6.9'));
}

/** CTA bosishlarida — docs/TELEGRAM.md. Eski klientda jim o'tadi. */
export function haptic(style: 'light' | 'medium' | 'heavy' = 'light'): void {
  if (!supportsModernApi()) return;
  tg?.HapticFeedback?.impactOccurred(style);
}

export function hapticSuccess(): void {
  if (!supportsModernApi()) return;
  tg?.HapticFeedback?.notificationOccurred('success');
}

/**
 * Efir va qo'ng'iroq davomida yoqiladi, tugagach o'chiriladi.
 * Tasodifan yopib, suhbatdan chiqib ketishning oldini oladi.
 */
export function setClosingConfirmation(enabled: boolean): void {
  if (!tg) return;
  if (enabled) tg.enableClosingConfirmation();
  else tg.disableClosingConfirmation();
}

/** Diamond xaridi — ilova ichida FAQAT Stars (D-008). */
export function openInvoice(url: string, cb?: (status: string) => void): void {
  tg?.openInvoice(url, cb);
}

/** Click — Mini App ichida ochilmaydi, tashqi brauzerda (D-008). */
export function openExternal(url: string): void {
  if (tg) tg.openLink(url);
  else window.open(url, '_blank', 'noopener');
}
